import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { GAME_ASSET_SLUG } from "@/config/game";
import { Button, ChipButton } from "@/design-system/Button/Button";
import Card, { BlurCard } from "@/design-system/Card/Card";
import { SortDropdown } from "@/design-system/Dropdown/SortDropdown";
import {
  PlayerCardWithMetaRating,
  PlayerCardWithMetaRatingSkeleton,
} from "@/design-system/PlayerCard/PlayerCard";
import {
  trackChooseClubCountry,
  trackExplore,
  trackViewContent,
} from "@/events/frontPageMixpanel";
import { cn } from "@/lib/utils";
import {
  FavoriteSource,
  activeFavoriteClubTabSelector,
  fetchFavoriteClubPlayers,
  fetchFavoritesFeed,
  frontPageSelector,
  initFavoritesFlow,
  setActiveFavoriteClubTab,
} from "@/redux/frontPage/frontPageSlice";
import { loadedContentSelector } from "@/redux/loadedContent/loadedContentSlice";
import { FAVORITE_CLUBS_AND_COUNTRIES_KEY } from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import {
  userFavoriteClubIdsSelector,
  userFavoriteCountryIdSelector,
} from "@/redux/user/userSlice";
import type { PlayersSortByType } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { Check, Pencil } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  CAROUSEL_CARD_SCALE,
  FrontPageContent,
  GRID_CARD_SCALE,
} from "../FrontPageContent";
import { ViewAllButton } from "../buttons/ViewAllButton";
import { FavoritesSheet } from "./FavoritesSheet";

const TARGET_CARD_COUNT = 5;
const TEASER_CLUB_ID = 243;

const CARD_VISIBILITY = [
  "",
  "hidden mobile:block",
  "hidden mobile:block",
  "hidden touch-device:block",
  "hidden large-desktop:block",
  "hidden large-desktop:block",
];

const playerKey = (player: { resourceId: number; evolutionData?: any }) =>
  player.resourceId + (player.evolutionData?.evolutionPath?.join("-") ?? "");

const sameSource = (a: FavoriteSource, b: FavoriteSource) => {
  if (a === "all" || b === "all") return a === b;
  return a.type === b.type && a.id === b.id;
};

/* The orders My Club's favourites page offers; newest first by default. */
const SORTS = [
  { value: "date-added", label: "players:toolbar.sort.new" },
  { value: "meta-rating", label: "players:toolbar.sort.metaRating" },
  {
    value: "meta-rating-3-chem",
    label: "players:toolbar.sort.metaRating3Chem",
  },
  { value: "rating", label: "players:toolbar.sort.rating" },
] as const;
const EVOLUTION_SORTS = [
  ...SORTS,
  { value: "upgrade", label: "players:toolbar.sort.upgrade" },
] as const;

export const ForYou = ({
  initialSource = "all",
  sortable = false,
  hideHeading = false,
  onSourceChange,
}: {
  /** Which favourite the feed starts narrowed to. */
  initialSource?: FavoriteSource;
  /** Lets the user pick the order (My Club); the front page keeps its own. */
  sortable?: boolean;
  /** Drops "For You" and its subtitle where the page already names it. */
  hideHeading?: boolean;
  /** Told when the user picks another favourite, e.g. to change page. */
  onSourceChange?: (source: FavoriteSource) => void;
} = {}) => {
  const { t } = useTranslation(["frontPage", "players"]);
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const activeTab = useSelector(activeFavoriteClubTabSelector);
  const favoriteClubIds = useSelector(userFavoriteClubIdsSelector);
  const favoriteCountryId = useSelector(userFavoriteCountryIdSelector);
  const { clubs, countries } = useSelector(loadedContentSelector);
  const navigate = useNavigate();
  const {
    favoritesFeedPlayers,
    favoritesFeedStatus,
    favoriteClubPlayers,
    favoriteClubPlayersStatus,
  } = useSelector(frontPageSelector);
  const dispatch = useDispatch<AppDispatch>();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [source, setSource] = useState<FavoriteSource>(initialSource);
  const [pickedSort, setSort] = useState<PlayersSortByType>("date-added");
  // Follows the side panel: Favorite club and Favorite country share a page.
  useEffect(() => setSource(initialSource), [initialSource]);

  const hasFavorites = favoriteClubIds.length > 0 || Boolean(favoriteCountryId);

  // "Evolution Players" opens the Evolutions hub; "Players" and "Past & Present"
  // both land on the top-level Players listing.
  const isEvolutionsTab = activeTab === "evolution-players";
  const sorts = isEvolutionsTab ? EVOLUTION_SORTS : SORTS;
  // A sort only one tab has (Upgrade) falls back to New on the others.
  const sort = sorts.some((option) => option.value === pickedSort)
    ? pickedSort
    : "date-added";

  const favoriteClubs = useMemo(
    () =>
      favoriteClubIds
        .map((id) => (clubs ?? []).find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => Boolean(c)),
    [favoriteClubIds, clubs],
  );
  const favoriteCountry = useMemo(
    () => (countries ?? []).find((c) => c.id === favoriteCountryId) ?? null,
    [countries, favoriteCountryId],
  );

  // Reset the source filter if the current one no longer applies.
  useEffect(() => {
    if (source === "all") return;
    const stillValid =
      source.type === "club"
        ? favoriteClubIds.includes(source.id)
        : source.id === favoriteCountryId;
    if (!stillValid) setSource("all");
  }, [favoriteClubIds, favoriteCountryId, source]);

  // Feed for the filled section; teaser data when no favorites are set.
  useEffect(() => {
    if (hasFavorites) {
      dispatch(
        fetchFavoritesFeed({
          source,
          activeTab,
          sort: sortable ? sort : undefined,
        }),
      );
    } else {
      dispatch(
        fetchFavoriteClubPlayers({
          clubId: TEASER_CLUB_ID,
          activeTab: "players",
        }),
      );
    }
  }, [
    hasFavorites,
    favoriteClubIds,
    favoriteCountryId,
    source,
    activeTab,
    sortable,
    sort,
    dispatch,
  ]);

  const openSheet = () => {
    dispatch(
      initFavoritesFlow({
        clubIds: favoriteClubIds,
        countryId: favoriteCountryId ?? null,
      }),
    );
    setSheetOpen(true);
  };

  // Only the "Choose Favorites" CTA reports the user-investment event; the
  // pencil button opens the same sheet to edit an existing selection.
  const handleChooseFavorites = () => {
    trackChooseClubCountry();
    openSheet();
  };

  const trackPlayer = (player: { newPlayer?: boolean }) =>
    trackViewContent({
      subsection: "For You",
      content_type: isEvolutionsTab ? "evo_player" : "player",
      new_content: player.newPlayer,
    });

  const handleViewAll = () => {
    trackExplore({
      destination_section: isEvolutionsTab ? "Evolutions" : "Players",
      destination_subsection: isEvolutionsTab ? "Players" : "Overview",
      subsection: "For You",
    });

    // Past & Present is a club-only concept, so it never takes the country into
    // account — not as the OR flag, not as a `country` param.
    const isPnp = activeTab === "pnp";

    let clubPath = "";
    let countryPath = "";
    let favoritesPath = "";
    if (source === "all") {
      // Separate club + country params AND together on the backend. When both
      // favorites are set, hand over the same flag the feed above uses so the
      // listing OR-matches them instead.
      if (!isPnp && favoriteClubs.length > 0 && favoriteCountryId) {
        favoritesPath = `${FAVORITE_CLUBS_AND_COUNTRIES_KEY}=1`;
      } else if (favoriteClubs.length > 0) {
        clubPath = `club=${favoriteClubs.map((club) => club.id).join(",")}`;
      } else if (favoriteCountryId) {
        countryPath = `country=${favoriteCountryId}`;
      }
    } else if (source.type === "club") {
      clubPath = `club=${source.id}`;
    } else if (source.type === "country") {
      countryPath = `country=${source.id}`;
    }
    // A sort the user picked carries over, so the list opens in that order.
    const sortPath = sortable ? `sort=${sort}` : "";
    const path = `?${[favoritesPath, clubPath, countryPath, sortPath].filter(Boolean).join("&")}`;

    if (activeTab === "players") {
      navigate("/players" + path);
    } else if (activeTab === "evolution-players") {
      navigate("/evolutions/players" + path);
    } else if (isPnp) {
      navigate("/players" + path + "&pastAndPresentClubs=1");
    }
  };

  const pnpActive = activeTab === "pnp";
  const countrySelected =
    (source !== "all" && source.type === "country") ||
    favoriteClubs.length === 0;

  // Clickable favorite avatars. No "all" entry — deselecting resets to "all".
  const favoriteSources = [
    ...favoriteClubs.map((club) => ({
      key: `club-${club.id}`,
      label: club.name,
      src: `${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/dark/${club.id}.png`,
      source: { type: "club" as const, id: club.id },
      isCountry: false,
    })),
    ...(favoriteCountry
      ? [
          {
            key: `country-${favoriteCountry.id}`,
            label: favoriteCountry.name,
            src: `${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${favoriteCountry.id}.png`,
            source: { type: "country" as const, id: favoriteCountry.id },
            isCountry: true,
          },
        ]
      : []),
  ];

  const contentPicker = (
    <div className="flex flex-row gap-x-2">
      <ChipButton
        selected={activeTab === "players"}
        onClick={() => dispatch(setActiveFavoriteClubTab("players"))}
        className={cn(
          "data-[selected=true]:text-amber-400",
          "data-[selected=true]:bg-amber-900/50",
          "data-[selected=true]:border-amber-900",
        )}
      >
        {t("tabs.players")}
      </ChipButton>
      <ChipButton
        selected={activeTab === "evolution-players"}
        onClick={() => dispatch(setActiveFavoriteClubTab("evolution-players"))}
        className={cn(
          "data-[selected=true]:text-emerald-400",
          "data-[selected=true]:bg-emerald-900/50",
          "data-[selected=true]:border-emerald-900",
        )}
      >
        {t("tabs.evolutionPlayers")}
      </ChipButton>
      {(() => {
        const pnpChip = (
          <ChipButton
            selected={activeTab === "pnp"}
            disabled={countrySelected}
            onClick={() => dispatch(setActiveFavoriteClubTab("pnp"))}
            className={cn(
              "data-[selected=true]:text-neutral-100",
              "data-[selected=true]:bg-neutral-700",
              "data-[selected=true]:border-neutral-600",
            )}
          >
            {t("tabs.pastAndPresent")}
          </ChipButton>
        );
        return countrySelected ? (
          <Tooltip
            content={t("forYou.pastAndPresentCountryTooltip")}
            delayDuration={100}
          >
            {pnpChip}
          </Tooltip>
        ) : (
          pnpChip
        );
      })()}
      <div className="ml-auto flex items-center gap-3">
        {sortable && (
          <SortDropdown
            items={sorts.map(({ value, label }) => ({
              value,
              label: t(label),
            }))}
            selected={sort}
            onChange={(value) => setSort(value as PlayersSortByType)}
          />
        )}
        <ViewAllButton onClick={handleViewAll} />
      </div>
    </div>
  );

  return (
    <section className="flex flex-col gap-y-4">
      {hasFavorites ? (
        <div className="flex flex-col gap-3">
          {!hideHeading && (
            <div className="flex flex-col gap-2">
              <h1 className="text-h1">{t("sections.forYou")}</h1>
              <span className="text-xs text-neutral-500">
                {activeTab === "players"
                  ? t("forYou.subtitle.players")
                  : activeTab === "evolution-players"
                    ? t("forYou.subtitle.evolutionPlayers")
                    : t("forYou.subtitle.pastAndPresent")}
              </span>
            </div>
          )}

          <div className="flex items-center gap-3.5">
            <div className="flex items-center gap-2.5">
              {favoriteSources.map((fav) => {
                const selected = sameSource(source, fav.source);
                const disabled = fav.isCountry && pnpActive;
                const avatar = (
                  <FavoriteAvatar
                    src={fav.src}
                    label={fav.label}
                    isCountry={fav.isCountry}
                    selected={selected}
                    disabled={disabled}
                    onClick={() => {
                      const next = selected ? "all" : fav.source;
                      setSource(next);
                      onSourceChange?.(next);
                    }}
                  />
                );
                return disabled ? (
                  <Tooltip
                    key={fav.key}
                    content={t("forYou.pastAndPresentDisableTooltip", {
                      name: fav.label,
                    })}
                    delayDuration={100}
                  >
                    {avatar}
                  </Tooltip>
                ) : (
                  <span key={fav.key} className="inline-flex">
                    {avatar}
                  </span>
                );
              })}
            </div>

            <Button
              variant="secondary"
              size="small"
              onClick={openSheet}
              icon={<Pencil size={16} />}
              className="rounded-full"
            />
          </div>
        </div>
      ) : (
        // The intro lives in the empty-state panel below, not up here.
        <h1 className="text-h1">{t("sections.forYou")}</h1>
      )}

      {hasFavorites ? (
        <FrontPageContent
          key={activeTab}
          header={contentPicker}
          status={favoritesFeedStatus}
          items={favoritesFeedPlayers}
          limitToVisible
          getKey={playerKey}
          renderGridCard={(player) => (
            <PlayerCardWithMetaRating
              hideLabel
              fullWidth
              scale={GRID_CARD_SCALE}
              player={player}
              asLink={{ basePath: "/players" }}
              onClick={() => trackPlayer(player)}
            />
          )}
          renderCarouselCard={(player) => (
            <PlayerCardWithMetaRating
              scale={CAROUSEL_CARD_SCALE}
              player={player}
              asLink={{ basePath: "/players" }}
              onClick={() => trackPlayer(player)}
            />
          )}
          renderGridSkeleton={() => (
            <PlayerCardWithMetaRatingSkeleton
              hideLabel
              scale={GRID_CARD_SCALE}
              fullWidth
            />
          )}
        />
      ) : (
        <div className="relative">
          <div className="flex gap-4">
            {favoriteClubPlayers
              .slice(0, TARGET_CARD_COUNT)
              .map((player, i) => (
                // Backdrop only: a light wash here reads as a loading skeleton,
                // and a heavier blur leaves nothing recognisable to tease.
                <BlurCard
                  key={i}
                  blurPx={8}
                  washClassName="bg-background/45"
                  className={cn(
                    "min-w-[210px] mobile:max-w-[320px] flex-1",
                    CARD_VISIBILITY[i],
                  )}
                  backing={<PlayerCardWithMetaRating player={player} />}
                />
              ))}
            {favoriteClubPlayersStatus === "pending" &&
              favoriteClubPlayers.length === 0 &&
              Array.from({ length: TARGET_CARD_COUNT }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "min-w-[210px] mobile:max-w-[320px] flex-1",
                    CARD_VISIBILITY[i],
                  )}
                >
                  <PlayerCardWithMetaRatingSkeleton fullWidth />
                </div>
              ))}
          </div>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
            <Card
              hoverable={false}
              className="pointer-events-auto flex max-w-sm flex-col items-center gap-3 px-6 py-5 text-center"
            >
              <h2 className="text-h4">{t("forYou.emptyTitle")}</h2>
              <span className="text-body text-foreground-faint">
                {t("forYou.intro")}
              </span>
              <Button
                variant="primary"
                size="large"
                onClick={handleChooseFavorites}
              >
                {t("forYou.chooseFavorites")}
              </Button>
            </Card>
          </div>
        </div>
      )}

      {/* Portalled, so it opens over everything wherever the section sits. */}
      {createPortal(
        <FavoritesSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
        />,
        document.body,
      )}
    </section>
  );
};

type FavoriteAvatarProps = {
  src: string;
  label: string;
  isCountry: boolean;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
};

const FavoriteAvatar = ({
  src,
  label,
  isCountry,
  selected,
  disabled,
  onClick,
}: FavoriteAvatarProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    aria-pressed={selected}
    className={cn(
      "relative flex size-11 items-center justify-center border-2 transition-colors",
      isCountry
        ? "rounded-full"
        : "rounded-[10px] not-supports-[corner-shape:squircle]:rounded-[5px] [corner-shape:squircle]",
      selected
        ? "border-blue-500"
        : "border-transparent hover:border-neutral-700",
      disabled && "opacity-40",
    )}
  >
    <img
      src={src}
      alt={label}
      className={cn(
        "size-9",
        // Squircle instead of a circle so non-round crests keep their sides.
        isCountry
          ? "rounded-full object-cover"
          : "rounded-md object-contain not-supports-[corner-shape:squircle]:rounded-[3px] [corner-shape:squircle]",
      )}
    />
    {selected && (
      <span className="absolute -right-1 -top-1 flex size-[15px] items-center justify-center rounded-full bg-blue-500 text-white">
        <Check size={9} strokeWidth={3} />
      </span>
    )}
  </button>
);
